import { BankDocumentsRoutes, BookingsRoutes, ReservationsRoutes } from '../../../Router/path';
import type { BankDocumentReservationCreateContext } from '../Types/bankDocumentReservationCreate.types';

interface BankDocumentReservationCreateNavigationParams
  extends BankDocumentReservationCreateContext {
  bookingId?: number;
  reservationId?: number;
}

const isPositiveInteger = (value: unknown): value is number =>
  Number.isInteger(value) && Number(value) > 0;

const parsePositiveInteger = (value: string | null): number | null => {
  if (!value) {
    return null;
  }

  const parsedValue = Number(value);
  return isPositiveInteger(parsedValue) ? parsedValue : null;
};

const normalizeReturnTo = (value: string | null | undefined): string => {
  if (!value || !value.startsWith('/') || value.startsWith('//')) {
    return BankDocumentsRoutes.queue;
  }

  return value;
};

export const buildBankDocumentReservationCreateQuery = (
  context: BankDocumentReservationCreateContext,
  params?: {
    bookingId?: number;
    reservationId?: number;
  }
): string => {
  const queryParams = new URLSearchParams();

  queryParams.set('origin', context.origin);
  queryParams.set('entryId', context.entryId.toString());
  queryParams.set('bankDocumentId', context.bankDocumentId.toString());
  queryParams.set('returnTo', normalizeReturnTo(context.returnTo));

  if (isPositiveInteger(context.companyId)) {
    queryParams.set('companyId', context.companyId.toString());
  }

  if (isPositiveInteger(params?.bookingId)) {
    queryParams.set('bookingId', params!.bookingId!.toString());
  }

  if (isPositiveInteger(params?.reservationId)) {
    queryParams.set('reservationId', params!.reservationId!.toString());
  }

  return queryParams.toString();
};

export const parseBankDocumentReservationCreateQuery = (
  input: string | URLSearchParams
): BankDocumentReservationCreateNavigationParams | null => {
  const queryParams = typeof input === 'string' ? new URLSearchParams(input) : input;

  const origin = queryParams.get('origin');
  if (origin !== 'bank-documents') {
    return null;
  }

  const entryId = parsePositiveInteger(queryParams.get('entryId'));
  const bankDocumentId = parsePositiveInteger(queryParams.get('bankDocumentId'));

  if (!entryId || !bankDocumentId) {
    return null;
  }

  const companyId = parsePositiveInteger(queryParams.get('companyId'));
  const bookingId = parsePositiveInteger(queryParams.get('bookingId'));
  const reservationId = parsePositiveInteger(queryParams.get('reservationId'));

  return {
    origin,
    entryId,
    bankDocumentId,
    companyId,
    returnTo: normalizeReturnTo(queryParams.get('returnTo')),
    bookingId: bookingId ?? undefined,
    reservationId: reservationId ?? undefined,
  };
};

export const buildCreateReservationRouteFromBankDocumentContext = (
  context: BankDocumentReservationCreateContext,
  bookingId: number
): string => {
  const query = buildBankDocumentReservationCreateQuery(context, { bookingId });
  return `${ReservationsRoutes.create}?${query}`;
};

export const buildCreateBookingRouteFromBankDocumentContext = (
  context: BankDocumentReservationCreateContext
): string => {
  const query = buildBankDocumentReservationCreateQuery(context);
  return `${BookingsRoutes.create}?${query}`;
};

export const buildBankDocumentsQueueReturnUrl = (params: {
  entryId: number;
  bankDocumentId: number;
  reservationId: number;
  companyId?: number | null;
}): string => {
  const queryParams = new URLSearchParams();
  queryParams.set('origin', 'bank-documents');
  queryParams.set('entryId', params.entryId.toString());
  queryParams.set('bankDocumentId', params.bankDocumentId.toString());
  queryParams.set('reservationId', params.reservationId.toString());

  if (isPositiveInteger(params.companyId)) {
    queryParams.set('companyId', params.companyId!.toString());
  }

  return `${BankDocumentsRoutes.queue}?${queryParams.toString()}`;
};
